import { Policy } from "./types";
import { SectionHeader } from "../../SectionHeader";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PolicyEditDialog } from "./PolicyEditDialog";
import { PolicyCard } from "./card/PolicyCard";

interface PolicyListProps {
  policies: Policy[];
  isVisible?: boolean;
  onToggle?: (sectionName: string) => void;
}

export const PolicyList = ({ policies, isVisible = true, onToggle }: PolicyListProps) => {
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [deletingPolicyId, setDeletingPolicyId] = useState<string | null>(null);
  const { toast } = useToast();

  if (!policies.length) {
    return null;
  }

  const handleDelete = async (policyId: string) => {
    try {
      setDeletingPolicyId(policyId);
      const { error } = await supabase
        .from('policies')
        .delete()
        .eq('id', policyId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Policy deleted successfully",
      });

      // Instead of reloading the page, we could update the local state
      // This would be a better UX, but for now we'll keep the existing behavior
      window.location.reload();
    } catch (error) {
      console.error('Error deleting policy:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete policy",
      });
      setDeletingPolicyId(null); // Reset deleting state on error
    }
  };

  return (
    <div className="space-y-4 bg-gradient-to-br from-white to-purple-50 rounded-lg border border-purple-100/50 shadow-lg p-6">
      <SectionHeader
        title="Existing Policies"
        sectionName="policies"
        isVisible={isVisible}
        onToggle={onToggle}
      />
      <div className="grid gap-4 animate-fade-up">
        {policies.map((policy) => (
          <PolicyCard
            key={policy.id}
            policy={policy}
            onEdit={setEditingPolicy}
            onDelete={handleDelete}
            isDeleting={deletingPolicyId === policy.id}
          />
        ))}
      </div>
      <PolicyEditDialog 
        policy={editingPolicy} 
        onClose={() => setEditingPolicy(null)} 
        onSave={() => {
          setEditingPolicy(null);
          window.location.reload();
        }}
      />
    </div>
  );
};
